/** Pointer capture and motion for Sheet; its scroll body owns its own gestures. */
import { useCallback, useEffect, useRef, type PointerEvent, type MouseEvent } from "react";
import { sheetDragAction, sheetDragOffset, sheetDragThresholdPx } from "@/lib/sheet_drag";
import { prefersReducedMotion } from "@/lib/motion";
import { haptics } from "@/lib/haptics";

const DESKTOP_QUERY = "(min-width: 768px)";
const DRAG_SLOP_PX = 10;
const VELOCITY_WINDOW_MS = 100;
interface Drag { id: number; start: number; offset: number; moved: boolean; armed: boolean; samples: { y: number; time: number }[] }

export function useSheetDrag(onClose: () => void) {
  const dialog = useRef<HTMLDivElement>(null);
  const scrim = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const swallow = useRef(false);
  const closing = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const callback = useRef(onClose);
  useEffect(() => { callback.current = onClose; }, [onClose]);
  const matches = (query: string): boolean => typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  const close = useCallback((): void => {
    if (closing.current) return;
    closing.current = true;
    const node = dialog.current;
    if (!node || prefersReducedMotion()) { callback.current(); return; }
    node.dataset.phase = "exit";
    node.style.removeProperty("transform");
    if (scrim.current) { scrim.current.dataset.phase = "exit"; scrim.current.style.removeProperty("opacity"); }
    // Read the CSS token so the fallback and visual duration cannot drift.
    const duration = Number(getComputedStyle(node).getPropertyValue("--sheet-exit-ms").trim());
    timer.current = window.setTimeout(() => callback.current(), Number.isFinite(duration) ? duration : 0);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const end = (event: PointerEvent<HTMLElement>, commit: boolean): void => {
    const state = drag.current;
    if (!state || state.id !== event.pointerId) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    const moved = state.moved;
    swallow.current = moved;
    window.setTimeout(() => { swallow.current = false; }, 0);
    const last = state.samples.at(-1);
    const first = state.samples[0];
    const elapsed = last && first ? event.timeStamp - first.time : 0;
    const velocity = elapsed > 0 && last && first ? (event.clientY - first.y) / elapsed : 0;
    const height = dialog.current?.getBoundingClientRect().height ?? 0;
    if (commit && moved && sheetDragAction(state.offset, height, velocity) === "dismiss") { close(); return; }
    if (dialog.current) { dialog.current.dataset.phase = "settle"; dialog.current.style.removeProperty("transform"); }
    if (scrim.current) { scrim.current.dataset.phase = "settle"; scrim.current.style.removeProperty("opacity"); }
  };
  return {
    dialog, scrim, close,
    onClickCapture: (event: MouseEvent<HTMLElement>): void => {
      if (!swallow.current) return;
      swallow.current = false;
      event.preventDefault(); event.stopPropagation();
    },
    headerProps: {
      onPointerDown: (event: PointerEvent<HTMLElement>): void => {
        if (matches(DESKTOP_QUERY) || closing.current || event.button !== 0 || drag.current) return;
        drag.current = { id: event.pointerId, start: event.clientY, offset: 0, moved: false, armed: false, samples: [{ y: event.clientY, time: event.timeStamp }] };
      },
      onPointerMove: (event: PointerEvent<HTMLElement>): void => {
        const state = drag.current;
        if (!state || state.id !== event.pointerId) return;
        state.offset = event.clientY - state.start;
        if (Math.abs(state.offset) < DRAG_SLOP_PX && !state.moved) return;
        state.moved = true;
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.setPointerCapture(event.pointerId);
        // Keep the sample preceding the velocity window, including a single fast move.
        while (state.samples.length > 1) {
          const next = state.samples[1];
          if (!next || event.timeStamp - next.time <= VELOCITY_WINDOW_MS) break;
          state.samples.shift();
        }
        state.samples.push({ y: event.clientY, time: event.timeStamp });
        const height = dialog.current?.getBoundingClientRect().height ?? 0;
        const armed = state.offset >= sheetDragThresholdPx(height);
        if (armed && !state.armed) haptics("threshold");
        state.armed = armed;
        if (dialog.current) {
          dialog.current.dataset.phase = "drag";
          dialog.current.style.transform = prefersReducedMotion() ? "none" : `translateY(${String(sheetDragOffset(state.offset))}px)`;
        }
        if (scrim.current) { scrim.current.dataset.phase = "drag"; scrim.current.style.opacity = String(1 - Math.min(1, Math.max(0, state.offset) / Math.max(1, height))); }
      },
      onPointerUp: (event: PointerEvent<HTMLElement>): void => end(event, true),
      onPointerCancel: (event: PointerEvent<HTMLElement>): void => end(event, false),
      onLostPointerCapture: (event: PointerEvent<HTMLElement>): void => end(event, false),
    },
  };
}
